import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardAuthRequest,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, RedirectRequest } from '@azure/msal-browser';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Store } from '@ngrx/store';
import { addSeconds } from 'date-fns';
import { filter, lastValueFrom, map, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ConfigService } from '../../../core/services/config/config.service';
import { User } from '../../../user/models/user';
import { getUser } from '../../../user/store/actions/user.actions';
import { AppInsightsFeatureType } from '../../models/app-insights-feature-type';
import { clearAllState, setGlobalError } from '../../store/actions/app.actions';
import { ThemeType } from '../../types/types';
import { AppInsightsService } from '../app-insights/app-insights.service';
import { AuthCookieService } from '../auth-cookie/auth-cookie.service';
import { SessionContextService } from '../session-context/session-context.service';
import { UtilityService } from '../utility/utility.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private msalService = inject(MsalService);
  private store = inject(Store);
  private authCookieService = inject(AuthCookieService);
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private msalBroadcastService = inject(MsalBroadcastService);
  private utilityService = inject(UtilityService);
  private appInsightsService = inject(AppInsightsService);
  private configService = inject(ConfigService);
  private sessionContextService = inject(SessionContextService);
  private get config() {
    return this.configService.config;
  }
  public isAuthComplete = signal(false);
  public theme?: ThemeType;

  private destroySubject = new Subject<void>();
  private loginRequest: MsalGuardAuthRequest = this.msalGuardConfig.authRequest as MsalGuardAuthRequest;
  private activeAccountId: string | null = null;
  private tokenExpirationDate = addSeconds(new Date(), 30); // initializing with valid date to prevent false negatives on app load

  private readonly LOGIN_ATTEMPT_KEY = 'patient_portal_login_attempt_count';
  private readonly MSAL_FAILURE_EVENT_TYPES: EventType[] = [EventType.ACQUIRE_TOKEN_FAILURE, EventType.LOGIN_FAILURE];
  private readonly MSAL_FATAL_ERROR_CODES = {
    EndpointsResolution: 'endpoints_resolution_error',
    InvalidInstance: 'invalid_instance',
    InvalidClient: 'invalid_client',
    UnauthorizedClient: 'unauthorized_client',
    RedirectUriMismatch: 'redirect_uri_mismatch',
    InvalidScope: 'invalid_scope',
    AuthorityMismatch: 'authority_mismatch',
    NetworkError: 'network_error',
  };

  initialize(): void {
    if (environment.allowAuthBypass && this.config.localAuthBypass) {
      this.activateLocalBypass();
      return;
    }
    this.msalService.instance.initialize().then(() => {
      this.activeAccountId = this.activeAccount?.homeAccountId ?? null;
      this.msalService.instance.handleRedirectPromise().then(authResult => {
        if (authResult) {
          this.onAuthenticationSuccess(authResult, false);
        } else if (this.activeAccount) {
          this.acquireTokenSilent();
        } else {
          this.login();
        }
      });
    });

    this.monitorMsalBroadcast();
    this.msalService.instance.enableAccountStorageEvents();
  }

  get tokenExpiration() {
    return this.tokenExpirationDate;
  }

  async isAuthenticated(): Promise<boolean> {
    if (environment.allowAuthBypass && this.config.localAuthBypass) {
      return true;
    }
    const url = this.config.loginApiUrl + '/Authorize';
    return await lastValueFrom(this.utilityService.getRequest(url).pipe(map(() => true))).catch(() => false);
  }

  clearCache(): void {
    this.store.dispatch(clearAllState());
    this.msalService.instance.clearCache();
    this.redirectToLogin();
  }

  login(): void {
    // will login without redirect if KMSI active; redirect otherwise
    let req = this.loginRequest ? { ...(this.loginRequest as RedirectRequest) } : undefined;
    if (this.theme) {
      req = { ...(req ?? ({} as RedirectRequest)) };
      req.extraQueryParameters = { ...(req.extraQueryParameters ?? {}), theme: this.theme };
    }
    this.msalService.loginRedirect(req);
  }

  logout(): void {
    this.store.dispatch(clearAllState());
    this.appInsightsService.trackEvent('Auth.Logout.Started', { feature: AppInsightsFeatureType.AUTH });
    this.appInsightsService.clearAuthenticatedUser();
    this.authCookieService.deleteCookie();
    this.sessionContextService.clear();
    this.msalService.logoutRedirect({
      account: this.activeAccount,
    });
  }

  refreshToken(): void {
    this.acquireTokenSilent(false);
  }

  private get activeAccount() {
    return this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];
  }

  private set tokenExpiration(expiresOn: Date) {
    this.tokenExpirationDate = expiresOn;
  }

  private acquireTokenSilent(requireAuthorization: boolean = true): void {
    this.appInsightsService.trackEvent('Auth.SilentLogin.Started', { feature: AppInsightsFeatureType.AUTH });
    this.msalService.instance
      .acquireTokenSilent({
        scopes: this.config.loginApiConfig.scopes,
        account: this.activeAccount,
        prompt: 'none',
        forceRefresh: false,
      })
      .then(authResult => this.onAuthenticationSuccess(authResult, true, requireAuthorization));
  }

  private onAuthenticationSuccess(
    authResult: AuthenticationResult | null,
    isSilentLogin: boolean,
    requireAuthorization: boolean = true
  ): void {
    if (authResult) {
      const incomingId = authResult.account.homeAccountId;
      if (this.activeAccountId !== null && this.activeAccountId !== incomingId) {
        this.store.dispatch(clearAllState());
      }
      this.activeAccountId = incomingId;
      sessionStorage.removeItem(this.LOGIN_ATTEMPT_KEY);
      const eventName = isSilentLogin ? 'Auth.SilentLogin.Success' : 'Auth.Login.Success';
      this.appInsightsService.trackEvent(eventName, { feature: AppInsightsFeatureType.AUTH });
      this.msalService.instance.setActiveAccount(authResult.account);
      this.tokenExpiration = new Date(authResult.expiresOn ?? 0);
      this.authCookieService.buildCookie(authResult.accessToken, this.tokenExpiration);
      // Capture the Entra session id for the audit X-Session-Id header (fallback handled downstream).
      const claims = authResult.idTokenClaims as Record<string, unknown> | undefined;
      this.sessionContextService.setFromClaim(claims?.['sid'] as string | undefined);
      if (requireAuthorization)
        this.getAuthorization(authResult.idTokenClaims as Record<string, unknown> | undefined, isSilentLogin);
    }
  }

  private getAuthorization(claims: Record<string, unknown> | undefined, isSilentLogin: boolean): void {
    if (claims) {
      const user: User = {
        azureId: claims['oid'],
        firstName: claims['first_name'] ?? claims['given_name'],
        lastName: claims['last_name'] ?? claims['family_name'],
        emailAddress: claims['email'],
      } as User;
      this.store.dispatch(
        getUser({
          user,
          isSilentLogin,
        })
      );
    }
  }

  private monitorMsalBroadcast(): void {
    // MSAL emits events for each phase of an interaction. Detect failure events to determine next steps (retry login, show error, etc)
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(event => this.MSAL_FAILURE_EVENT_TYPES.includes(event.eventType)),
        takeUntil(this.destroySubject)
      )
      .subscribe((event: EventMessage) => {
        // if error is fatal, set global error and redirect
        if (
          'errorCode' in (event.error || {}) &&
          Object.values(this.MSAL_FATAL_ERROR_CODES).includes((event.error as { errorCode: string }).errorCode)
        ) {
          console.error('Fatal MSAL error occurred:', event.error);
          this.appInsightsService.trackTrace('Auth.Login.FatalError', SeverityLevel.Error, {
            feature: AppInsightsFeatureType.AUTH,
            error: event.error,
          });
          this.isAuthComplete.set(true);
          sessionStorage.removeItem(this.LOGIN_ATTEMPT_KEY);
          this.store.dispatch(setGlobalError({ error: event.error }));
        } else {
          // if error is not fatal, attempt login again (up to 3 times) before redirecting to error page
          this.appInsightsService.trackEvent('Auth.Login.Failed', { feature: AppInsightsFeatureType.AUTH });
          this.incrementLoginAttemptCount();
          if (this.hasExceededLoginAttempts()) {
            this.appInsightsService.trackTrace('Auth.Login.FatalError', SeverityLevel.Error, {
              feature: AppInsightsFeatureType.AUTH,
              error: event.error,
            });
            this.isAuthComplete.set(true);
            sessionStorage.removeItem(this.LOGIN_ATTEMPT_KEY);
            this.store.dispatch(setGlobalError({ error: 'Exceeded login redirect attempts' }));
          } else {
            this.login();
          }
        }
      });
  }

  private hasExceededLoginAttempts(): boolean {
    const currentCount = parseInt(sessionStorage.getItem(this.LOGIN_ATTEMPT_KEY) || '0', 10);
    return currentCount >= 3;
  }

  private incrementLoginAttemptCount(): void {
    const currentCount = parseInt(sessionStorage.getItem(this.LOGIN_ATTEMPT_KEY) || '0', 10);
    sessionStorage.setItem(this.LOGIN_ATTEMPT_KEY, (currentCount + 1).toString());
  }

  private redirectToLogin(): void {
    window.location.assign('/'); //refresh to force login redirect
  }

  private activateLocalBypass(): void {
    const hostname = window.location.hostname;
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isPrivateLan = /^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.)/.test(hostname);

    if (!isLocalHost && !isPrivateLan) {
      throw new Error('[LOCAL AUTH BYPASS] Not permitted outside localhost or private LAN.');
    }

    const token = this.config.localAuthToken;
    const user = this.config.localAuthUser;

    if (!token || !user?.azureId) {
      throw new Error('[LOCAL AUTH BYPASS] localAuthToken and localAuthUser must be set in config.local.json.');
    }

    this.authCookieService.buildCookie(token, addSeconds(new Date(), 3600));
    this.store.dispatch(getUser({ user, isSilentLogin: false }));
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
